import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'designSystem/lib/utils'
import { Slot } from 'radix-ui'
import * as React from 'react'

/**
 * Standard typography primitive. Prefer `<Text variant="...">` over ad-hoc
 * `font-size`/`font-weight`/`line-height` in styled-components.
 *
 * `variant` selects a named preset (size + weight + leading + tracking) and is
 * the standard knob. The remaining props are escape hatches for one-off
 * overrides; because they emit the same Tailwind class groups, `tailwind-merge`
 * (via `cn`) drops the preset's class and keeps the override.
 */
const textVariants = cva('', {
  variants: {
    variant: {
      display: 'text-3xl font-bold tracking-tight leading-tight',
      title: 'text-xl font-semibold tracking-tight',
      heading: 'text-lg font-semibold',
      subheading: 'text-base font-medium',
      body: 'text-sm font-normal leading-normal',
      bodyLg: 'text-base font-normal leading-normal',
      label: 'text-sm font-medium',
      caption: 'text-xs font-normal',
      overline: 'text-xxs font-medium uppercase tracking-wide',
      code: 'font-mono text-sm',
    },
    color: {
      default: 'text-foreground',
      muted: 'text-muted-foreground',
      tertiary: 'text-muted-foreground/70',
      secondary: 'text-secondary-foreground',
      disabled: 'text-muted-foreground opacity-60',
      primary: 'text-primary',
      destructive: 'text-destructive',
      inherit: 'text-inherit',
    },
    size: {
      xxs: 'text-xxs',
      xs: 'text-xs',
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
      '5xl': 'text-5xl',
    },
    weight: {
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
    leading: {
      none: 'leading-none',
      tight: 'leading-tight',
      normal: 'leading-normal',
      relaxed: 'leading-relaxed',
    },
    tracking: {
      tight: 'tracking-tight',
      normal: 'tracking-normal',
      wide: 'tracking-wide',
    },
    transform: {
      uppercase: 'uppercase',
      capitalize: 'capitalize',
      none: 'normal-case',
    },
    wrap: {
      inherit: '',
      normal: 'whitespace-normal',
      nowrap: 'text-nowrap',
      balance: 'text-balance',
      pretty: 'text-pretty',
    },
    truncate: {
      true: 'truncate',
    },
    italic: {
      true: 'italic',
    },
    numeric: {
      tabular: 'tabular-nums',
    },
  },
  defaultVariants: {
    variant: 'body',
    wrap: 'inherit',
  },
})

type TextElement =
  | 'span'
  | 'strong'
  | 'p'
  | 'div'
  | 'label'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'ul'
  | 'ol'
  | 'li'
  | 'code'

const Text = React.forwardRef<
  HTMLElement,
  React.ComponentPropsWithoutRef<'span'> &
    VariantProps<typeof textVariants> & {
      /** Semantic element to render. Defaults to `span`. */
      as?: TextElement
      asChild?: boolean
    }
>(
  (
    {
      className,
      as = 'span',
      variant,
      color,
      size,
      weight,
      align,
      leading,
      tracking,
      transform,
      wrap,
      truncate,
      italic,
      numeric,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const Comp: React.ElementType = asChild ? Slot.Root : as

    return (
      <Comp
        data-slot='text'
        data-variant={variant}
        className={cn(
          textVariants({
            variant,
            color,
            size,
            weight,
            align,
            leading,
            tracking,
            transform,
            wrap,
            truncate,
            italic,
            numeric,
          }),
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Text.displayName = 'Text'

export { Text, textVariants }
